// Service to fetch analytics data from the backend API
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Fetches user events from Firebase Analytics via BigQuery
 * @param {string} uid - User's Firebase UID
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time
 * @param {string[]} eventNames - Optional filter by event names
 * @param {number} limit - Max events to return (default 1000)
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object>} Analytics data
 */
export const fetchUserEvents = async (uid, startTime, endTime, eventNames = null, limit = 1000, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const body = {
      uid,
      startTime: startTime instanceof Date ? startTime.toISOString() : startTime,
      endTime: endTime instanceof Date ? endTime.toISOString() : endTime,
      limit
    };

    if (eventNames && eventNames.length > 0) {
      body.eventNames = eventNames;
    }

    const response = await fetch(buildApiUrl('/analytics/user-events'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch analytics: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || 'Error fetching user events';
    if (addError) addError(errorMsg, error.stack, 'Analytics API');
    console.error('Error fetching user events:', error);
    throw error;
  }
};

/**
 * Fetches aggregated event counts for a user
 * @param {string} uid - User's Firebase UID
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object>} Event counts
 */
export const fetchEventCounts = async (uid, startTime, endTime, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/analytics/event-counts'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({
        uid,
        startTime: startTime instanceof Date ? startTime.toISOString() : startTime,
        endTime: endTime instanceof Date ? endTime.toISOString() : endTime
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch event counts: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || 'Error fetching event counts';
    if (addError) addError(errorMsg, error.stack, 'Analytics API');
    console.error('Error fetching event counts:', error);
    throw error;
  }
};

/**
 * Fetches session data for a user
 * @param {string} uid - User's Firebase UID
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object>} Session data
 */
export const fetchSessions = async (uid, startTime, endTime, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/analytics/sessions'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({
        uid,
        startTime: startTime instanceof Date ? startTime.toISOString() : startTime,
        endTime: endTime instanceof Date ? endTime.toISOString() : endTime
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch sessions: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || 'Error fetching sessions';
    if (addError) addError(errorMsg, error.stack, 'Analytics API');
    console.error('Error fetching sessions:', error);
    throw error;
  }
};

/**
 * Fetches timeline events for a specific session (on-demand)
 * @param {string} uid - User's Firebase UID
 * @param {string} sessionId - GA4 session ID
 * @param {string} sessionDate - Date of the session (YYYY-MM-DD) for efficient table filtering
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object>} Session timeline with events
 */
export const fetchSessionTimeline = async (uid, sessionId, sessionDate, addError = null, { offset = 0, limit = 1000 } = {}) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    // Use the new endpoint that includes Crashlytics data
    const response = await fetch(buildApiUrl('/analytics/session-timeline-with-crashes'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({
        uid,
        sessionId,
        sessionDate,
        offset,
        limit
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch session timeline: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || 'Error fetching session timeline';
    if (addError) addError(errorMsg, error.stack, 'Analytics API');
    console.error('Error fetching session timeline:', error);
    throw error;
  }
};

const EXPORT_PAGE_SIZE = 5000; // server-side max for /session-timeline-with-crashes

/** Sessions loaded at once by fetchSessionEventsBetween. */
const SESSION_FETCH_CONCURRENCY = 4;

/**
 * Fetches every page of a session's timeline (analytics events + correlated Crashlytics
 * crashes) and returns them merged into one response-shaped object.
 *
 * The endpoint looks up crashes per page using that page's own time window plus a 5 minute
 * buffer, so adjacent pages can return the same crash; crashes are de-duplicated by
 * crashDetails.eventId here.
 *
 * @param {Function} onProgress - called with (loadedAnalyticsEvents, totalAnalyticsEvents)
 */
export const fetchFullSessionTimeline = async (uid, sessionId, sessionDate, { onProgress } = {}) => {
  const analyticsEvents = [];
  const crashesById = new Map();
  let first = null;
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let page;
    try {
      page = await fetchSessionTimeline(uid, sessionId, sessionDate, null, {
        offset,
        limit: EXPORT_PAGE_SIZE
      });
    } catch (error) {
      error.exportContext = {
        endpoint: '/analytics/session-timeline-with-crashes',
        offset,
        limit: EXPORT_PAGE_SIZE,
        pageNumber: offset / EXPORT_PAGE_SIZE + 1,
        loadedAnalyticsEvents: analyticsEvents.length,
        totalAnalyticsEvents: first ? first.totalAnalyticsEvents : null,
        crashesSoFar: crashesById.size
      };
      throw error;
    }
    if (!first) first = page;

    let pageAnalyticsCount = 0;
    for (const event of page.events) {
      if (event.isCrash) {
        crashesById.set(event.crashDetails?.eventId ?? `${event.timestamp}-${crashesById.size}`, event);
      } else {
        analyticsEvents.push(event);
        pageAnalyticsCount += 1;
      }
    }

    hasMore = page.hasMore;
    if (hasMore && pageAnalyticsCount === 0) {
      const error = new Error(`Session timeline page at offset ${offset} returned no events but reported more remaining`);
      error.exportContext = {
        endpoint: '/analytics/session-timeline-with-crashes',
        offset,
        limit: EXPORT_PAGE_SIZE,
        pageNumber: offset / EXPORT_PAGE_SIZE + 1,
        loadedAnalyticsEvents: analyticsEvents.length,
        totalAnalyticsEvents: first.totalAnalyticsEvents,
        crashesSoFar: crashesById.size
      };
      throw error;
    }
    offset += EXPORT_PAGE_SIZE;
    if (onProgress) onProgress(analyticsEvents.length, first.totalAnalyticsEvents);
  }

  const crashes = Array.from(crashesById.values());
  const events = [...analyticsEvents, ...crashes].sort((a, b) => {
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeA - timeB;
  });

  return {
    uid,
    sessionId,
    events,
    totalAnalyticsEvents: first.totalAnalyticsEvents,
    analyticsEventCount: analyticsEvents.length,
    crashCount: crashes.length,
    fatalCrashCount: crashes.filter(c => c.isFatal).length,
    complete: analyticsEvents.length === first.totalAnalyticsEvents
  };
};

/**
 * Every event of every app session overlapping a time window, for views that
 * span several sessions. A session that fails to load is skipped and counted.
 * Crashes are de-duplicated by crashDetails.eventId, since neighbouring
 * sessions can return the same crash.
 *
 * @param {string} uid - User's Firebase UID
 * @param {number} startMs - Window start
 * @param {number} endMs - Window end
 * @returns {Promise<{ sessions: Array<Object>, events: Array<Object>, failedSessionCount: number }>}
 */
export const fetchSessionEventsBetween = async (uid, startMs, endMs) => {
  const { sessions = [] } = await fetchSessions(uid, new Date(startMs), new Date(endMs));

  const events = [];
  const crashIds = new Set();
  let failedSessionCount = 0;
  let nextSession = 0;

  const loadNext = async () => {
    while (nextSession < sessions.length) {
      const session = sessions[nextSession++];
      try {
        const sessionDate = new Date(session.startTime).toISOString().slice(0, 10);
        const timeline = await fetchFullSessionTimeline(uid, session.sessionId, sessionDate);
        for (const event of timeline.events) {
          const crashId = event.isCrash ? event.crashDetails?.eventId : null;
          if (crashId && crashIds.has(crashId)) continue;
          if (crashId) crashIds.add(crashId);
          events.push(event);
        }
      } catch (error) {
        failedSessionCount++;
        console.error(`Error loading session ${session.sessionId}:`, error);
      }
    }
  };
  await Promise.all(Array.from({ length: SESSION_FETCH_CONCURRENCY }, loadNext));

  return { sessions, events, failedSessionCount };
};
